import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { RootState } from 'App'
import { useParams } from 'react-router-dom'
import {
  BusinessGoal,
  BusinessGoalDateFilter,
  BusinessGoalMilestone,
  BusinessGoalPlan,
  UpdateUserTaskInput,
  UserTask,
  UserTaskItemStatus,
  UserTaskQuickFilter,
  useGetBusinessGoalQuery,
  useGetBusinessProfileQuery,
  useGetUserTasksQuery,
  useUpdateUserTaskMutation,
  useUpdateBusinessGoalMilestoneMutation,
  UpdateBusinessGoalMilestoneInput,
  useUpdateGoalPlanMutation,
  UpdateGoalPlanInput,
  useGetAdvisorAssignedTasksQuery,
} from '__generated__/api-types-and-hooks'
import { otherGoalIds, myOtherTasksGoal } from 'appConfig/data'
import { GOALIDS } from 'appConfig/enums'
import { createWeeklyMilestones } from 'appUtils'
import moment, { Moment } from 'moment'
import { useSelector } from 'react-redux'
import { getTenantId } from 'utils/getTenantId'
import { getHoursCeiling } from 'components/AppKanbanBoard/AppTask/AppTaskHook/AppTaskHook.hook'
import { calculateGoalProgress } from 'components/AppDataReport/AppData/appUtils'
import { isAdvisorRole } from 'utils/helper'

interface IUseGetBusinessGoalInput {
  filter?: BusinessGoalDateFilter
  goalId: string
  clientId?: string
}

export interface IHandlePlanStateTrackingInput {
  goalId: string
  planId: string
  fumbledState?: boolean
  microCelebrationState?: boolean
}

export const useGetUserBusinessGoal = (input: IUseGetBusinessGoalInput) => {
  const clientProfileLoading = useSelector(
    (state: RootState) => state.loadingStatus.clientDetailsLoading
  )
  const userId = useSelector((state: RootState) => state.user.user.id)
  const { clientId } = useParams()
  const tenantId = getTenantId()
  const [businessGoal, setBusinessGoal] = useState<BusinessGoal>()
  const [hasGoalStarted, setHasGoalStarted] = useState(false)
  const [goalProgress, setGoalProgress] = useState(0)
  const [weekProgress, setWeekProgress] = useState(0)
  const [thisWeekTasks, setThisWeekTasks] = useState<Array<UserTask>>([])
  const [filteredTasks, setFilteredTasks] = useState<Array<UserTask>>([])
  const [isTouchdown, setIsTouchdown] = useState(false)
  const [isPlanCompleted, setIsPlanCompleted] = useState(false)
  const [isFumbled, setIsFumbled] = useState(false)
  const [isMicroCelebration, setIsMicroCelebration] = useState(false)
  const [currentWeekName, setCurrentWeekName] = useState<string>('')
  const [plan, setPlan] = useState<BusinessGoalPlan>()
  const [currentMilestone, setCurrentMilestone] = useState<BusinessGoalMilestone>()
  const [nextMilestone, setNextMilestone] = useState<BusinessGoalMilestone>()
  const [planStartDate, setPlanStartDate] = useState('')
  const [planEndDate, setPlanEndDate] = useState('')
  const [goalStartDate, setGoalStartDate] = useState('')
  const [goalEndDate, setGoalEndDate] = useState('')
  const [totalEstimatedEffortInMinutes, setTotalEstimatedEffortInMinutes] = useState(0)
  const [dedicatedHoursPerWeek, setDedicatedHoursPerWeek] = useState<number | undefined>()
  const isAllTasks = otherGoalIds.includes(input.goalId as GOALIDS)
  const celebrationThresholds = [25, 50, 75]
  const today = useRef(new Date())
  const { mutate: updateUserTask } = useUpdateUserTaskMutation()
  const { mutate: updateBusinessGoalMilestone } = useUpdateBusinessGoalMilestoneMutation()
  const { mutate: updateBusinessGoalPlan } = useUpdateGoalPlanMutation()

  const handleUserTaskCarryOver = (task: UserTask) => {
    const input: UpdateUserTaskInput = {
      taskId: task.taskId!,
      tenantId: task.tenantId!,
      userId: task.userId,
      isCarryOver: true,
    }
    updateUserTask({ input })
  }

  const handleBusinessGoalMilestoneFumble = (milestone: BusinessGoalMilestone) => {
    const input: UpdateBusinessGoalMilestoneInput = {
      milestoneId: milestone.milestoneId ?? '',
      planId: milestone.planId ?? '',
      isFumbled: true,
    }

    updateBusinessGoalMilestone({ input })
  }

  const handlePlanStateTracking = ({
    goalId,
    planId,
    fumbledState,
    microCelebrationState,
  }: IHandlePlanStateTrackingInput) => {
    const input: UpdateGoalPlanInput = {
      planId,
      goalId,
      isUndismissedFumbledState: fumbledState,
      isUndismissedMicroCelebrationState: microCelebrationState,
    }

    updateBusinessGoalPlan({ input })
  }

  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date)
    result.setDate(result.getDate() + days)
    return result
  }

  // Function to convert milestone index to week name
  const getWeekName = (milestoneIndex: number) => {
    const weekNames = [
      'Week One',
      'Week Two',
      'Week Three',
      'Week Four',
      'Week Five',
      'Week Six',
      'Week Seven',
      'Week Eight',
      'Week Nine',
      'Week Ten',
      'Week Eleven',
      'Week Twelve',
      'Week Thirteen',
      'Week Fourteen',
      'Week Fifteen',
      'Week Sixteen',
      'Week Seventeen',
      'Week Eithteen',
      'Week Nineteen',
      'Week Twenty',
    ]
    return weekNames[milestoneIndex] || `Week ${milestoneIndex + 1}`
  }

  // Fetch business profile
  const { data: businessData } = useGetBusinessProfileQuery(
    { id: clientId || userId },
    { refetchOnWindowFocus: false }
  )

  // Get logo from businessData
  const companyLogo = useMemo(() => {
    return businessData?.getBusinessProfile?.logo?.url
  }, [businessData])

  // Get business name
  const businessName = useMemo(() => businessData?.getBusinessProfile?.businessName, [businessData])
  const businessOwner = useMemo(
    () => businessData?.getBusinessProfile?.users?.find((user) => user.userId === clientId),
    [businessData?.getBusinessProfile?.users, clientId]
  )

  const {
    data,
    isLoading: isBusinessGoalLoading,
    isSuccess,
    refetch: refetchBusinessGoal,
  } = useGetBusinessGoalQuery(
    {
      input: {
        tenantId,
        userId: clientId || userId,
        goalId: input?.goalId,
        filter: { dateRange: input.filter },
      },
    },
    {
      refetchOnWindowFocus: false,
      // goalId is required (ID!) by GetBusinessGoalInput — without it AppSync
      // rejects the request, so don't fire until we have one.
      enabled: !isAllTasks && !!input?.goalId && !clientProfileLoading && (!!clientId || !!userId),
    }
  )

  const {
    data: tasks,
    refetch,
    isLoading: isTasksLoading,
  } = useGetUserTasksQuery(
    {
      input: {
        tenantId,
        userId: clientId ?? userId,
        filter: {
          quickFilter:
            input?.goalId === 'all-plays'
              ? UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
        },
      },
    },
    {
      enabled: isAllTasks,
    }
  )

  const { data: advisorTasks, refetch: refetchAdvisorTasks } = useGetAdvisorAssignedTasksQuery(
    {
      input: {
        tenantId,
        userId,
        filter: {
          quickFilter:
            input?.goalId === 'all-plays'
              ? UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
        },
      },
    },
    {
      enabled: isAllTasks && isAdvisorRole() && !clientId,
    }
  )

  // let today = new Date()
  useEffect(() => {
    if (!isAllTasks && data && data.getBusinessGoal) {
      const goal = data.getBusinessGoal as BusinessGoal
      const plan = data.getBusinessGoal.plan as BusinessGoalPlan
      data.getBusinessGoal.plan?.milestones?.sort((a, b) => Number(a.order) - Number(b.order))
      data.getBusinessGoal.plan?.milestones?.forEach((milestone) => {
        milestone.tasks?.sort((a, b) => Number(a.order) - Number(b.order))
      })
      const planTasks = plan?.tasks as UserTask[]
      setFilteredTasks(planTasks)
      setBusinessGoal(goal)
      setGoalStartDate(goal.startDate ?? '')
      setGoalEndDate(goal.expectedEndDate ?? '')
      setPlan(plan)
      setPlanStartDate(plan.startDate ?? '')
      setPlanEndDate(plan.expectedEndDate ?? '')
      setIsPlanCompleted(
        planTasks
          .filter(
            (at) =>
              at?.status !== UserTaskItemStatus.Archived &&
              at?.status !== UserTaskItemStatus.Deleted
          )
          .every((task) => task.status === UserTaskItemStatus.Done)
      )

      if (goal.experimentalCurrentDay) {
        const currentDay = addDays(new Date(), goal.experimentalCurrentDay)
        today.current = currentDay
      }

      if (goal.dedicatedHoursPerWeek) {
        let dedicatedHoursPerWeek = goal.dedicatedHoursPerWeek
        if (goal.resourcesAverageDedicatedTimePerWeek) {
          dedicatedHoursPerWeek += goal.resourcesAverageDedicatedTimePerWeek
        }
        setDedicatedHoursPerWeek(dedicatedHoursPerWeek)
      }

      // Update goal progress
      if (goal.plan && goal.plan.tasks) {
        let remainingTaskEffortInMinutes = 0
        goal.plan.tasks?.forEach((task) => {
          const estimatedEffortInMinutes = task?.estimatedEffortInMinutes
          if (task?.status !== UserTaskItemStatus.Done) {
            remainingTaskEffortInMinutes += estimatedEffortInMinutes ?? 0
          }
        })
        setGoalProgress(
          calculateGoalProgress(
            planTasks.filter(
              (at) =>
                at?.status !== UserTaskItemStatus.Archived &&
                at?.status !== UserTaskItemStatus.Deleted
            )
          )
        )
        setTotalEstimatedEffortInMinutes(remainingTaskEffortInMinutes)
        setHasGoalStarted(
          goal.status !== UserTaskItemStatus.Todo ||
            goal.plan?.tasks?.some((task) => task?.status !== UserTaskItemStatus.Todo)
        )
      }

      // Compute current week's tasks
      if (goal.plan && goal.plan.milestones) {
        let currentMilestoneIndex = 0
        let currentMilestone = goal.plan.milestones[0]

        // If goal is still in TODO, first milestone is the current one
        if (goal.status === UserTaskItemStatus.Todo) {
          const currentMilestone = goal.plan.milestones[0]
          const currentTasks = currentMilestone?.tasks ?? []
          setThisWeekTasks(currentTasks.filter((task) => task.status !== UserTaskItemStatus.Done))
          if (input.filter === BusinessGoalDateFilter.ThisWeek) {
            setFilteredTasks(currentTasks)
          }

          // Set week progress for the first milestone
          let completedWeekTasks = 0
          let totalWeekTasks = 0
          currentTasks.forEach((task) => {
            const estimatedEffortInMinutes = getHoursCeiling(task?.estimatedEffortInMinutes)
            if (task?.status === UserTaskItemStatus.Done) {
              completedWeekTasks += estimatedEffortInMinutes ?? 0
            }
            totalWeekTasks += estimatedEffortInMinutes ?? 0
          })
          setWeekProgress(Math.round((completedWeekTasks / totalWeekTasks) * 100))
        } else {
          const firstMilestone = goal.plan.milestones[0]
          const lastMilestone = goal.plan.milestones[goal.plan.milestones.length - 1]
          const firstMilestoneStart = new Date(firstMilestone?.startDate ?? '')
          const lastMilestoneEnd = new Date(firstMilestone?.expectedEndDate ?? '')
          if (today.current < firstMilestoneStart) {
            currentMilestoneIndex = 0
            currentMilestone = firstMilestone
          }

          if (today.current > lastMilestoneEnd) {
            currentMilestoneIndex = goal.plan.milestones.length - 1
            currentMilestone = lastMilestone
          }

          // Find the milestone where today's date falls
          goal.plan.milestones.forEach((milestone, index) => {
            const milestoneStart = new Date(milestone.startDate ?? '')
            const milestoneEnd = new Date(milestone.expectedEndDate ?? '')

            const isDoneStatus = milestone.status === UserTaskItemStatus.Done
            const allTasksDone = milestone.tasks?.every(
              (task) => task.status === UserTaskItemStatus.Done
            )

            if (today.current >= milestoneStart && today.current <= milestoneEnd) {
              currentMilestoneIndex = index
              currentMilestone = milestone
            }

            if (
              (isDoneStatus || allTasksDone) &&
              goal.plan!.milestones![index + 1]?.status !== UserTaskItemStatus.Todo
            ) {
              currentMilestoneIndex = index + 1
              currentMilestone = goal.plan!.milestones![index + 1]
              if (!currentMilestone) {
                currentMilestoneIndex = index
                currentMilestone = milestone
              }
            }
          })

          // Carry over uncompleted tasks from previous milestones
          const carryOverTasks: UserTask[] = []
          const tasksToUpdate: UserTask[] = [] // Array to store tasks that need to be marked as carryover
          const fumbledMilestones: BusinessGoalMilestone[] = [] // Array to store fumbled milestones

          for (let i = 0; i < currentMilestoneIndex; i++) {
            const milestone = goal.plan?.milestones[i]
            goal.plan.milestones[i].tasks?.forEach((task) => {
              if (task?.status !== UserTaskItemStatus.Done && !task.isCarryOver) {
                carryOverTasks.push(task)
                tasksToUpdate.push(task) // Tasks to mark as carryover

                // push prev milestone to fumbled array only if it is not already marked as fumbled
                if (!milestone.isFumbled) {
                  fumbledMilestones.push(milestone)
                }
              } else if (task?.isCarryOver) {
                carryOverTasks.push(task)
              }
            })
          }

          // Update carryover flag for tasks (using Promise.all for parallel invocation)
          if (tasksToUpdate.length > 0) {
            Promise.all(tasksToUpdate.map(handleUserTaskCarryOver))
          }

          // update db and mark fumbled milestones to true
          if (fumbledMilestones.length > 0) {
            setIsFumbled(true)
            Promise.all(fumbledMilestones.map(handleBusinessGoalMilestoneFumble))
            handlePlanStateTracking({
              goalId: goal.goalId!,
              planId: plan.planId!,
              fumbledState: true,
            })
          } else if (plan.isUndismissedFumbledState) {
            setIsFumbled(true)
          } else {
            setIsFumbled(false)
          }

          const percentCompletion = calculateWeekPercentCompletion(currentMilestone?.tasks || [])

          //   check if current milestone is completed and next milestone started so we can start next milestone
          if (
            percentCompletion >= 100 &&
            (goal.plan.milestones[currentMilestoneIndex + 1]?.status !== UserTaskItemStatus.Todo ||
              goal.plan.milestones[currentMilestoneIndex + 1]?.tasks?.some(
                (task) => task.status !== UserTaskItemStatus.Todo
              ))
          ) {
            currentMilestoneIndex++
            currentMilestone = goal.plan.milestones[currentMilestoneIndex]
            if (!currentMilestone) {
              currentMilestoneIndex--
              currentMilestone = goal.plan.milestones[currentMilestoneIndex]
            }
          }

          // Combine carryover tasks and current milestone tasks
          const currentTasks: UserTask[] = [...carryOverTasks, ...(currentMilestone?.tasks ?? [])]
          setThisWeekTasks(
            currentTasks
              .filter((task) => task.status !== UserTaskItemStatus.Done)
              .sort((a, b) => Number(a.order) - Number(b.order))
          )
          // Set week progress
          const weekPercentCompletion = calculateWeekPercentCompletion(currentTasks)
          setWeekProgress(weekPercentCompletion)

          // Mark touchdown if progress is 100%
          if (weekPercentCompletion >= 100) setIsTouchdown(true)
          else setIsTouchdown(false)

          // Micro Celebration Logic
          const previousProgress = Number(localStorage.getItem('previousWeekProgress')) || 0

          const isMicroCelebration = hasMicroCelebrationThresholdBeenReached(
            weekPercentCompletion,
            previousProgress,
            celebrationThresholds
          )
          plan.isUndismissedMicroCelebrationState
            ? setIsMicroCelebration(true)
            : setIsMicroCelebration(isMicroCelebration)
          if (isMicroCelebration) {
            handlePlanStateTracking({
              goalId: goal.goalId!,
              planId: plan.planId!,
              microCelebrationState: true,
            })
          }

          // Update localStorage with the current week progress
          localStorage.setItem('previousWeekProgress', weekPercentCompletion.toString())
        }

        // Set the name of the current week based on the milestone index
        const weekName = getWeekName(currentMilestoneIndex)
        setCurrentWeekName(weekName)

        // Set current and next milestones
        setCurrentMilestone(currentMilestone)
        currentMilestoneIndex++
        currentMilestone = goal.plan.milestones[currentMilestoneIndex]
        setNextMilestone(currentMilestone)
      }
    } else if (isAllTasks && (tasks?.getUserTasks || advisorTasks?.getAdvisorAssignedTasks)) {
      const otherTasks: UserTask[] = [
        ...(tasks?.getUserTasks || []),
        ...(advisorTasks?.getAdvisorAssignedTasks || []),
      ]
      const tasksByMilestones = createWeeklyMilestones(otherTasks)
      setPlanEndDate(getLatestEndDate(otherTasks))
      setGoalEndDate(getLatestEndDate(otherTasks))
      setGoalStartDate(getEarliestStartDate(otherTasks))

      const { remainingTaskEffortInMinutes } = calculateRemainingTaskEffortInMinutes(otherTasks)

      setGoalProgress(calculateGoalProgress(otherTasks))
      setTotalEstimatedEffortInMinutes(remainingTaskEffortInMinutes)

      let currentMilestoneOutstandingTasks: UserTask[]
      let currentMilestoneIndex: number
      let weeklyProgress: number
      if (!currentMilestone) {
        const nextMilestoneIndex = findFirstMilestoneIndexWithOutstandingTasks(tasksByMilestones)
        const nextMilestone = tasksByMilestones?.[nextMilestoneIndex]
        currentMilestoneOutstandingTasks =
          nextMilestone?.tasks?.filter((task) => task.status !== UserTaskItemStatus.Done) || []
        setCurrentMilestone(nextMilestone)
        currentMilestoneIndex = nextMilestoneIndex
        weeklyProgress = calculateWeekPercentCompletion(nextMilestone?.tasks || [])
      } else {
        currentMilestoneIndex = findMilestoneIndex(tasksByMilestones, currentMilestone)
        const updatedMilestone = tasksByMilestones[currentMilestoneIndex]
        currentMilestoneOutstandingTasks =
          updatedMilestone?.tasks?.filter((task) => task.status !== UserTaskItemStatus.Done) || []
        weeklyProgress = calculateWeekPercentCompletion(updatedMilestone?.tasks || [])
        setCurrentMilestone(updatedMilestone)
      }

      setThisWeekTasks(currentMilestoneOutstandingTasks)
      setCurrentWeekName(getWeekName(currentMilestoneIndex))
      setWeekProgress(weeklyProgress)

      if (currentMilestoneOutstandingTasks.length > 0) {
        const previousProgress = Number(localStorage.getItem('previousWeekProgressMisc')) || 0
        setIsMicroCelebration(
          hasMicroCelebrationThresholdBeenReached(
            weeklyProgress,
            previousProgress,
            celebrationThresholds
          )
        )
        localStorage.setItem('previousWeekProgressMisc', weeklyProgress.toString())
      } else {
        const nextMilestoneIndex = findFirstMilestoneIndexWithOutstandingTasks(tasksByMilestones)
        if (nextMilestoneIndex !== -1) {
          const nextMilestone = tasksByMilestones[nextMilestoneIndex]
          setNextMilestone(nextMilestone)
          setIsMicroCelebration(false)
          localStorage.setItem(
            'previousWeekProgressMisc',
            calculateWeekPercentCompletion(nextMilestone?.tasks || []).toString()
          )
        }
      }
      const tasksExist = (tasksByMilestones[currentMilestoneIndex]?.tasks || []).length > 0
      setIsTouchdown(weeklyProgress === 100 && tasksExist)
    }
    // eslint-disable-next-line
  }, [isSuccess, data?.getBusinessGoal, data, tasks, tasks?.getUserTasks, setNextMilestone])

  // Create a combined refetch function that handles both regular and advisor tasks
  const combinedRefetch = useCallback(async () => {
    // Create a union type of the specific promise return types
    type RefetchResult =
      | ReturnType<typeof refetch>
      | ReturnType<typeof refetchAdvisorTasks>
      | ReturnType<typeof refetchBusinessGoal>

    const results: RefetchResult[] = []

    if (isAllTasks) {
      // For "all tasks" view, call both refetch methods if available
      if (refetch) {
        results.push(refetch())
      }
      if (refetchAdvisorTasks) {
        results.push(refetchAdvisorTasks())
      }
    } else {
      // For specific goal view, call refetchBusinessGoal
      if (refetchBusinessGoal) {
        results.push(refetchBusinessGoal())
      }
    }

    return Promise.all(results)
  }, [refetch, refetchAdvisorTasks, refetchBusinessGoal, isAllTasks])

  return {
    businessGoal: isAllTasks
      ? myOtherTasksGoal.find((goal) => goal.goalId === input.goalId)
      : businessGoal,
    thisWeekTasks,
    goalProgress,
    weekProgress,
    isBusinessGoalLoading,
    hasGoalStarted: isAllTasks ? true : hasGoalStarted,
    refetchBusinessGoal: combinedRefetch,
    companyLogo,
    isTouchdown,
    isPlanCompleted,
    isFumbled,
    setIsFumbled,
    isMicroCelebration,
    currentWeekName,
    currentMilestone,
    setCurrentMilestone,
    nextMilestone,
    plan,
    today: today.current,
    planStartDate,
    planEndDate,
    totalEstimatedEffortInMinutes,
    filteredTasks,
    isAllTasks,
    dedicatedHoursPerWeek,
    goalStartDate,
    goalEndDate,
    businessName,
    businessOwner: businessOwner?.user,
    handlePlanStateTracking,
    isFetchingTasks: isBusinessGoalLoading || isTasksLoading,
  }
}

export const getLatestEndDate = (tasks: UserTask[]) => {
  if (tasks.length === 0) {
    return ''
  }

  let latestDate: string | Moment = ''

  for (const task of tasks) {
    if (!task.expectedEndDate) continue

    const taskEndDate = moment(task.expectedEndDate)
    if (!taskEndDate.isValid()) continue

    if (!latestDate || taskEndDate.isAfter(latestDate)) {
      latestDate = taskEndDate.format('YYYY-MM-DD')
    }
  }

  return latestDate
}

const calculateWeekPercentCompletion = (tasks: UserTask[]): number => {
  let completedWeekTasks = 0
  let totalWeekTasks = 0

  tasks.forEach((task) => {
    const estimatedEffortInMinutes = getHoursCeiling(task?.estimatedEffortInMinutes)
    if (task?.status === UserTaskItemStatus.Done) {
      completedWeekTasks += estimatedEffortInMinutes ?? 0
    }
    totalWeekTasks += estimatedEffortInMinutes ?? 0
  })

  const result = Math.round((completedWeekTasks / totalWeekTasks) * 100)
  if (isNaN(result)) {
    return 0
  }
  return result
}

const hasMicroCelebrationThresholdBeenReached = (
  currentProgress: number,
  previousProgress: number,
  thresholds: number[]
): boolean => {
  return thresholds.some(
    (threshold) =>
      currentProgress >= threshold &&
      previousProgress < threshold &&
      currentProgress < 100 &&
      currentProgress > 0
  )
}

const calculateRemainingTaskEffortInMinutes = (tasks: UserTask[]) => {
  return tasks.reduce(
    (acc, task) => {
      const estimatedEffortInMinutes = task?.estimatedEffortInMinutes
      if (task?.status === UserTaskItemStatus.Done) {
        acc.completedTaskEffortInMinutes += estimatedEffortInMinutes ?? 0
      } else {
        acc.remainingTaskEffortInMinutes += estimatedEffortInMinutes ?? 0
      }
      acc.totalTaskEffortInMinutes += estimatedEffortInMinutes ?? 0
      return acc
    },
    {
      completedTaskEffortInMinutes: 0,
      totalTaskEffortInMinutes: 0,
      remainingTaskEffortInMinutes: 0,
    }
  )
}

const findFirstMilestoneIndexWithOutstandingTasks = (milestones: BusinessGoalMilestone[]) => {
  return milestones.findIndex(
    (milestone) => milestone?.tasks?.some((task) => task.status !== UserTaskItemStatus.Done) // Find the first milestone with outstanding tasks
  )
}

const findMilestoneIndex = (
  milestones: BusinessGoalMilestone[],
  milestone: BusinessGoalMilestone
): number => {
  return milestones.findIndex((m) => m.startDate === milestone.startDate)
}

export const getEarliestStartDate = (tasks: UserTask[]) => {
  if (tasks.length === 0) {
    return ''
  }

  let earliestDate: string | Moment = ''

  for (const task of tasks) {
    if (!task.startDate) continue

    const taskStartDate = moment(task.startDate)
    if (!taskStartDate.isValid()) continue

    if (!earliestDate || taskStartDate.isBefore(earliestDate)) {
      earliestDate = taskStartDate.format('YYYY-MM-DD')
    }
  }

  return earliestDate
}
